/* eslint-disable @typescript-eslint/no-non-null-assertion */
import { useShowSnackbar } from "@octopusdeploy/design-system-components";
import { DeprecatedSelect } from "@octopusdeploy/design-system-octopus-components";
import type { TagResource, TagSetResource } from "@octopusdeploy/octopus-server-client";
import { OctopusError } from "@octopusdeploy/octopus-server-client";
import type * as React from "react";
import { useState, useEffect, useCallback, useRef } from "react";
import { repository } from "~/clientInstance";
import { MissingChip, RoleChip } from "~/components/Chips";
import { DeprecatedMultiSelect } from "~/components/MultiSelect/MultiSelect";
import type { SelectItem } from "~/components/VirtualListWithKeyboard/SelectItem";
interface TargetTagSetSelectProps {
    value: string[];
    onChange: (roles: string[]) => void;
    items?: TagSetResource[];
    fieldNameFormatter?: (tagSetName: string) => string;
    autoFocus?: boolean;
    respectTagSetType?: boolean;
    canAdd?: boolean;
}
const CREATE_PREFIX = "__create__";
const TagResourceMultiSelect = DeprecatedMultiSelect<TagResource>();
export const TargetTagSetSelect: React.FC<TargetTagSetSelectProps> = ({ value, onChange, items, fieldNameFormatter, autoFocus, respectTagSetType, canAdd }) => {
    const [tagSets, setTagSets] = useState<TagSetResource[] | null>(items ?? null);
    // Tracks pending create text per SingleSelect TagSet for Enter key support
    const singleSelectFilterText = useRef<Map<string, string>>(new Map());
    const showSnackbar = useShowSnackbar();
    useEffect(() => {
        if (items) {
            setTagSets(items);
            return;
        }
        async function loadTagSets() {
            const loadedTagSets = await repository.TagSets.getAllByScopes(["Target"]);
            const filteredTagSets = loadedTagSets.filter((ts) => ts.Type !== "FreeText").sort((a, b) => a.SortOrder - b.SortOrder);
            setTagSets(filteredTagSets);
        }
        loadTagSets();
    }, [items]);
    const validateNewTagName = useCallback((name: string, tagSet: TagSetResource): string | null => {
        if (!tagSet.IsSystem && name.includes("/"))
            return "Tag name cannot contain \"/\"";
        if (name.includes("|"))
            return "Tag name cannot contain \"|\"";
        const otherTagSet = tagSets?.find((ts) => ts.Id !== tagSet.Id && ts.Tags.some((t) => t.Name.toLowerCase() === name.toLowerCase()));
        if (otherTagSet)
            return `Tag "${name}" already exists in "${otherTagSet.Name}"`;
        return null;
    }, [tagSets]);
    const createTagImmediately = useCallback(async (tagSet: TagSetResource, tagName: string): Promise<TagSetResource | null> => {
        try {
            const fresh = await repository.TagSets.get(tagSet.Id);
            if (fresh.Tags.some((t) => t.Name === tagName)) {
                // Tag already exists on server, just update local state
                setTagSets((prev) => prev?.map((ts) => (ts.Id === fresh.Id ? fresh : ts)) ?? null);
                return fresh;
            }
            fresh.Tags.push({ Id: "", Name: tagName, Color: "#333333", Description: "", SortOrder: fresh.Tags.length, CanonicalTagName: `${fresh.Name}/${tagName}` });
            const updated = await repository.TagSets.modify(fresh);
            setTagSets((prev) => prev?.map((ts) => (ts.Id === updated.Id ? updated : ts)) ?? null);
            return updated;
        }
        catch (e) {
            const message = OctopusError.isOctopusError(e) ? e.ErrorMessage : e instanceof Error ? e.message : "An unexpected error occurred";
            showSnackbar(`Failed to create tag "${tagName}": ${message}`);
            return null;
        }
    }, [showSnackbar]);
    const handleNewTag = useCallback(async (tagSet: TagSetResource, tagName: string) => {
        if (validateNewTagName(tagName, tagSet))
            return;
        const updated = await createTagImmediately(tagSet, tagName);
        if (!updated)
            return;
        const otherTags = value.filter((role) => !updated.Tags.find((t) => t.Name === role) && role !== tagName);
        const currentTagsInSet = value.filter((role) => updated.Tags.find((t) => t.Name === role));
        onChange([...otherTags, ...currentTagsInSet, tagName]);
    }, [value, onChange, createTagImmediately, validateNewTagName]);
    const chipRenderer = (allTags: TagResource[], orphanNames: Set<string>, r: TagResource | SelectItem, onRequestDelete: () => void) => {
        const tag = allTags.find((t) => t.Id === r.Id);
        const name = tag?.Name ?? r.Name;
        if (orphanNames.has(name)) {
            return (<MissingChip lookupId={name} text={name} description={`Tag "${name}" does not exist in any tag set. You can remove it or recreate it by adding it to a tag set.`} onRequestDelete={onRequestDelete} deleteButtonAccessibleName={`Delete ${name}`}/>);
        }
        return <RoleChip onRequestDelete={onRequestDelete} deleteButtonAccessibleName={`Delete ${name}`} role={name} showContextualHelp/>;
    };
    const handleChange = (tagsInSet: TagResource[], tagIds: string[]) => {
        const selectedRolesFromOtherTagSets = value.filter((role) => {
            const tagInThisSet = tagsInSet.find((t) => t.Name === role);
            return !tagInThisSet;
        });
        const selectedRolesFromThisTagSet = tagIds.map((tagId) => {
            const tag = tagsInSet.find((t) => t.Id === tagId);
            return tag ? tag.Name : tagId;
        });
        onChange([...selectedRolesFromOtherTagSets, ...selectedRolesFromThisTagSet]);
    };
    const handleNewSingleSelectTag = useCallback(async (tagSet: TagSetResource, tagName: string) => {
        if (validateNewTagName(tagName, tagSet))
            return;
        const updated = await createTagImmediately(tagSet, tagName);
        if (!updated)
            return;
        // For SingleSelect: replace existing selection, don't append
        const otherTagSetValues = value.filter((role) => !updated.Tags.find((t) => t.Name === role) && role !== tagName);
        onChange([...otherTagSetValues, tagName]);
    }, [value, onChange, createTagImmediately, validateNewTagName]);
    const handleSingleSelectChange = (tagSet: TagSetResource, tagId: string | undefined) => {
        if (tagId?.startsWith(CREATE_PREFIX)) {
            const tagName = tagId.slice(CREATE_PREFIX.length);
            handleNewSingleSelectTag(tagSet, tagName);
            return;
        }
        handleChange(tagSet.Tags, tagId ? [tagId] : []);
    };
    const addNewTemplate = (tagSet: TagSetResource) => (text: string) => (<div>
            <div>
                {(() => {
            const err = validateNewTagName(text, tagSet);
            return err ? (<>{err}</>) : (<>
                            Press <code>Enter</code> to create &quot;{text}&quot;
                        </>);
        })()}
            </div>
        </div>);
    const renderSingleSelect = (ts: TagSetResource, sortedTags: TagResource[], selected: string[], fieldName: string, index: number) => {
        const selectItems = sortedTags.map((tag) => ({ value: tag.Id, text: tag.Name, description: tag.Description }));
        const handleEnterToCreate = canAdd
            ? (e: React.KeyboardEvent) => {
                if (e.key === "Enter") {
                    const pendingText = singleSelectFilterText.current.get(ts.Id);
                    if (pendingText && !validateNewTagName(pendingText, ts)) {
                        e.preventDefault();
                        handleNewSingleSelectTag(ts, pendingText);
                        singleSelectFilterText.current.delete(ts.Id);
                        document.activeElement?.dispatchEvent(new KeyboardEvent("keydown", { key: "Escape", bubbles: true }));
                    }
                }
            }
            : undefined;
        const handleFilterWithCreate = canAdd
            ? async (filterText: string) => {
                const matches = selectItems.filter((item) => item.text.toLowerCase().includes(filterText.toLowerCase()));
                const exactMatch = filterText.trim() && matches.some((item) => item.text.toLowerCase() === filterText.trim().toLowerCase());
                if (filterText.trim() && !exactMatch) {
                    const trimmed = filterText.trim();
                    const validationError = validateNewTagName(trimmed, ts);
                    if (!validationError) {
                        singleSelectFilterText.current.set(ts.Id, trimmed);
                        return [...matches, { value: `${CREATE_PREFIX}${trimmed}`, text: `Press Enter to create "${trimmed}"` }];
                    }
                    singleSelectFilterText.current.delete(ts.Id);
                    return [...matches, { value: "", text: validationError }];
                }
                singleSelectFilterText.current.delete(ts.Id);
                return matches;
            }
            : undefined;
        return (<div key={ts.Id} onKeyDown={handleEnterToCreate}>
                <DeprecatedSelect items={selectItems} value={selected.length > 0 ? selected[0] : ""} onChange={(tagId) => handleSingleSelectChange(ts, tagId)} allowFilter={true} allowClear={true} autoFocus={autoFocus && index === 0} label={`Select ${fieldName}`} sortItems={false} onFilterChanged={handleFilterWithCreate} empty={canAdd && ts.Tags.length === 0 ? "Create your first target tag, for example \"acme-web-server\"" : undefined}/>
            </div>);
    };
    if (!tagSets) {
        return null;
    }
    const allKnownTagNames = new Set(tagSets.flatMap((ts) => ts.Tags.map((t) => t.Name)));
    const orphanTagNames = value.filter((v) => !allKnownTagNames.has(v));
    const orphanNameSet = new Set(orphanTagNames);
    const systemTagSet = tagSets.find((ts) => ts.IsSystem);
    return (<div>
            {tagSets.map((ts, index) => {
            // Show orphan tags (values not in any tag set) as removable chips in the system tag set
            const orphanPlaceholders: TagResource[] = ts.Id === systemTagSet?.Id ? orphanTagNames.map((name) => ({ Id: name, Name: name, Color: "#333333", Description: "", SortOrder: 999, CanonicalTagName: `${ts.Name}/${name}` })) : [];
            const allTags = [...ts.Tags, ...orphanPlaceholders];
            if (allTags.length === 0 && !canAdd) {
                return null;
            }
            const selected = allTags.filter((t) => value.includes(t.Name)).map((t) => t.Id);
            const accessibleName = `targetTagSetFilter-${ts.Name}`;
            const fieldName = fieldNameFormatter ? fieldNameFormatter(ts.Name) : `${ts.Name} tags`;
            const sortedTags = allTags.sort((t1, t2) => t1.SortOrder - t2.SortOrder);
            const emptyMessage = canAdd && ts.Tags.length === 0 ? "Create your first target tag, for example \"acme-web-server\"" : undefined;
            if (respectTagSetType && ts.Type === "SingleSelect") {
                return renderSingleSelect(ts, sortedTags, selected, fieldName, index);
            }
            return (<TagResourceMultiSelect key={ts.Id} accessibleName={accessibleName} onChange={(tagIds) => handleChange(allTags, tagIds)} onNew={canAdd ? (tagName: string) => handleNewTag(ts, tagName) : null!} addNewTemplate={canAdd ? addNewTemplate(ts) : undefined} addNewOnBlur={canAdd} value={selected} items={sortedTags} renderChip={(r: TagResource | SelectItem, onDelete) => chipRenderer(allTags, orphanNameSet, r, onDelete)} fieldName={fieldName} description={ts.Description} autoFocus={autoFocus && index === 0} sortItems={false} placeholder={emptyMessage} empty={emptyMessage}/>);
        })}
        </div>);
};
TargetTagSetSelect.displayName = "TargetTagSetSelect"
